import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
// @mui
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
// utils
import { fToNow } from "../../../utils/formatTime";
import {
  useGetNotificationsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
} from "../../../features/notifications/notificationsApiSlice";
import {
  selectRealtimeNotifications,
  markNotificationAsRead,
} from "../../../features/notifications/notificationsSlice";
import useAuth from "../../../hooks/useAuth";
// components
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";

// ----------------------------------------------------------------------

const buildContractStatusActionUrl = (notification) => {
  const metadata = notification?.metadata || {};
  const memberId = String(metadata?.memberId || "").trim();
  if (!memberId) return "";

  const action = String(metadata?.action || "").trim().toLowerCase();
  const focusStatus =
    action === "reactivate"
      ? "active"
      : action === "terminate"
      ? "terminated"
      : "";

  const params = new URLSearchParams({
    focusMemberId: memberId,
  });

  if (action) params.set("focusAction", action);
  if (focusStatus) params.set("focusStatus", focusStatus);

  return `/dashboard/members/overview?${params.toString()}`;
};

const getNotificationActionUrl = (notification) => {
  if (notification?.type === "contract_status_change") {
    const deepLink = buildContractStatusActionUrl(notification);
    if (deepLink) return deepLink;
  }
  return notification?.actionUrl || "";
};

const getNotificationVisual = (notification) => {
  switch (notification?.type) {
    case "contract_status_change":
      return {
        icon: "mdi:account-sync-outline",
        avatarSx: {
          bgcolor: "warning.light",
          color: "warning.dark",
        },
        hint: "Mitglied anzeigen",
      };
    case "event_public":
      return {
        icon: "mdi:calendar-star",
        avatarSx: {
          bgcolor: "success.light",
          color: "success.dark",
        },
        hint: "Veranstaltung öffnen",
      };
    default:
      return {
        icon: "eva:bell-outline",
        avatarSx: {
          bgcolor: "primary.light",
          color: "primary.dark",
        },
        hint: "",
      };
  }
};

export default function NotificationsPopover() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAdmin, isSuperAdmin } = useAuth();
  const { data: apiNotifications = [] } = useGetNotificationsQuery();
  const realtimeNotifications = useSelector(selectRealtimeNotifications);
  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead, { isLoading: isMarkingAllAsRead }] =
    useMarkAllAsReadMutation();
  const [open, setOpen] = useState(null);

  // Combine real-time notifications with API notifications, prioritizing real-time
  const allNotifications = useMemo(() => {
    const realtimeIds = new Set(realtimeNotifications.map((n) => n._id));
    const apiOnly = apiNotifications.filter((n) => !realtimeIds.has(n._id));
    return [...realtimeNotifications, ...apiOnly];
  }, [realtimeNotifications, apiNotifications]);

  const unreadNotifications = useMemo(() => {
    return allNotifications.filter((n) => !n.isRead).slice(0, 10);
  }, [allNotifications]);

  const notificationCount = unreadNotifications.length;
  const canMarkAllAsRead = isAdmin || isSuperAdmin;

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleNotificationClick = async (notification) => {
    setOpen(null);

    // Try to mark as read, but navigate regardless of success/failure
    try {
      await markAsRead(notification._id).unwrap();
      // Update Redux state to mark notification as read
      dispatch(markNotificationAsRead(notification._id));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }

    const actionUrl = getNotificationActionUrl(notification);
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!canMarkAllAsRead || notificationCount === 0 || isMarkingAllAsRead) {
      return;
    }

    try {
      await markAllAsRead().unwrap();

      // Keep local real-time state aligned so unread count updates immediately.
      realtimeNotifications
        .filter((notification) => !notification.isRead)
        .forEach((notification) => {
          dispatch(markNotificationAsRead(notification._id));
        });
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  return (
    <>
      <IconButton
        color={open ? "primary" : "default"}
        onClick={handleOpen}
        sx={{ width: 40, height: 40 }}
        aria-label="Benachrichtigungen"
      >
        <Badge
          badgeContent={notificationCount}
          color="warning"
          overlap="circular"
          max={99}
          showZero={false}
        >
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            ml: 0.75,
            width: 380,
            overflow: "hidden",
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                bgcolor: notificationCount ? "warning.light" : "success.light",
                color: notificationCount ? "warning.dark" : "success.dark",
                width: 44,
                height: 44,
              }}
            >
              <Iconify
                icon={
                  notificationCount
                    ? "eva:bell-fill"
                    : "eva:checkmark-circle-2-fill"
                }
              />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1">Benachrichtigungen</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {notificationCount === 0
                  ? "Keine neuen Benachrichtigungen."
                  : notificationCount === 1
                  ? "1 neue Benachrichtigung."
                  : `${notificationCount} neue Benachrichtigungen.`}
              </Typography>
            </Box>
            {canMarkAllAsRead && notificationCount > 0 ? (
              <Typography
                component="button"
                type="button"
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAllAsRead}
                sx={{
                  border: 0,
                  bgcolor: "transparent",
                  p: 0,
                  m: 0,
                  cursor: isMarkingAllAsRead ? "default" : "pointer",
                  color: isMarkingAllAsRead ? "text.disabled" : "primary.main",
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                {isMarkingAllAsRead ? "Wird markiert..." : "Alle als gelesen"}
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        {notificationCount === 0 ? (
          <Box sx={{ px: 2.5, py: 3 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Alles erledigt. Neue Benachrichtigungen erscheinen hier sofort.
            </Typography>
          </Box>
        ) : (
          <Scrollbar sx={{ maxHeight: 340 }}>
            <List disablePadding>
              {unreadNotifications.map((notification) => {
                const visual = getNotificationVisual(notification);
                return (
                  <ListItem key={notification._id} disableGutters>
                    <ListItemButton
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={visual.avatarSx}>
                          <Iconify icon={visual.icon} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2">
                            {notification.message}
                          </Typography>
                        }
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.disabled" }}
                            >
                              {fToNow(notification.createdAt)}
                            </Typography>
                            {visual.hint ? (
                              <Chip
                                size="small"
                                label={visual.hint}
                                variant="outlined"
                                color="primary"
                                sx={{ height: 20 }}
                              />
                            ) : null}
                          </Stack>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Scrollbar>
        )}
      </Popover>
    </>
  );
}
